import React, { useState } from 'react'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/Card'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { useDispatch, useSelector } from 'react-redux'
import Logo from "../../assets/images/logo-dark.svg"
import { Tokens } from '../../constants'
import { register } from '../../store/actions/auth/register'
import Loading from '@/shared/common/Loading'
import { Check, X } from 'lucide-react'

// Mirrors the server's password rule (Joi in the backend, better-auth minimum
// underneath) so people are told before the round trip, not after.
const passwordChecks = [
    { key: 'length', label: 'At least 8 characters', test: (value) => value.length >= 8 },
    { key: 'upper', label: 'One uppercase letter', test: (value) => /[A-Z]/.test(value) },
    { key: 'lower', label: 'One lowercase letter', test: (value) => /[a-z]/.test(value) },
    { key: 'number', label: 'One number', test: (value) => /[0-9]/.test(value) },
]

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isLoading = useSelector(state => state.isLoading ?? null)
    const [error, setError] = useState({});
    // const token = localStorage.getItem(Tokens.ADMIN_TOKEN)
    const [userInfo, setUserInfo] = useState({
        name: "",
        email: '',
        password: '',
        confirm_password: '',
        organizationName: ''
    })

    // useEffect(() => {
    //   if (token) {
    //     navigate("/")
    //   }
    // }, []);

    const handleChange = (e) => {
        setUserInfo({ ...userInfo, [e.target.name]: e.target.value })
        // A field's error is stale the moment the user edits that field.
        if (error[e.target.name]) {
            setError((prev) => ({ ...prev, [e.target.name]: undefined }))
        }
    }

    const handleValidation = () => {
        let errors = {};
        let isValid = true;
        if (userInfo.email === '') {
            errors['email'] = 'Please enter your email';
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email.trim())) {
            errors['email'] = 'Please enter a valid email address';
            isValid = false;
        }
        if (userInfo.password === '') {
            errors['password'] = 'Please enter a password';
            isValid = false;
        } else if (passwordChecks.some((check) => !check.test(userInfo.password))) {
            errors['password'] = 'Your password does not meet all the requirements below';
            isValid = false;
        }
        if (userInfo.name === '') {
            errors['name'] = 'Please enter your name';
            isValid = false;
        } else if (userInfo.name.trim().length < 2) {
            errors['name'] = 'Name must be at least 2 characters';
            isValid = false;
        }
        if (userInfo.confirm_password === '') {
            errors['confirm_password'] = 'Please confirm your password';
            isValid = false;
        } else if (userInfo.confirm_password !== userInfo.password) {
            errors['confirm_password'] = 'Passwords do not match';
            isValid = false;
        }

        setError(errors);
        return isValid;
    };

    const handleSubmit = (e) => {
        if (handleValidation()) {
            dispatch(register(userInfo, navigate))
        }
    }
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };
    return (
        <div className='flex justify-center items-center  h-screen p-4'>
            <Card className="max-w-2xl w-full relative shadow-none rounded-sm">
                <div className='flex justify-center items-center pt-4'>
                    <img className='absolute -top-12 left-[30%] w-56' src={Logo} alt="logo image" />
                </div>
                <CardHeader>
                    <CardTitle className="text-2xl font-500">Sign up</CardTitle>
                    <CardDescription>
                    Enter your details to get started.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                    <div className="space-y-1">
                        <Label htmlFor="name">Name</Label>
                        <Input id="name" name="name" placeholder="Your Name..." onChange={(e) => handleChange(e)} onKeyDown={handleKeyPress} autoComplete='off' />
                        <div className={error['name'] ? "text-rose-600 text-xs" : ''}>{error['name'] ? error['name'] : null}</div>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="organizationName">Organization Name</Label>
                        <Input id="organizationName" name="organizationName" placeholder="Organization.." onChange={(e) => handleChange(e)} onKeyDown={handleKeyPress} autoComplete='off' />
                        <div className={error['organizationName'] ? "text-rose-600 text-xs" : ''}>{error['organizationName'] ? error['organizationName'] : null}</div>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" name="email" placeholder="name@gmail.com" onChange={(e) => handleChange(e)} onKeyDown={handleKeyPress} autoComplete='off' />
                        <div className={error['email'] ? "text-rose-600 text-xs" : ''}>{error['email'] ? error['email'] : null}</div>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="password">Password</Label>
                        <Input id="password" name="password" type="password" placeholder="********" onChange={(e) => handleChange(e)} onKeyDown={handleKeyPress} autoComplete='new-password' aria-describedby="password-requirements" />
                        <div className={error['password'] ? "text-rose-600 text-xs" : ''}>{error['password'] ? error['password'] : null}</div>
                        <ul id="password-requirements" className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 pt-1">
                            {passwordChecks.map((check) => {
                                const passed = check.test(userInfo.password)
                                return (
                                    <li key={check.key} className={`flex items-center gap-1.5 text-xs ${userInfo.password === '' ? 'text-gray-500' : passed ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {passed && userInfo.password !== ''
                                            ? <Check className="w-3 h-3" aria-hidden="true" />
                                            : <X className={`w-3 h-3 ${userInfo.password === '' ? 'invisible' : ''}`} aria-hidden="true" />}
                                        {check.label}
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="confirm_password">Confirm Password</Label>
                        <Input id="confirm_password" type="password" name="confirm_password" placeholder="********" onChange={(e) => handleChange(e)} onKeyDown={handleKeyPress} autoComplete='off' />
                        <div className={error['confirm_password'] ? "text-rose-600 text-xs" : ''}>{error['confirm_password'] ? error['confirm_password'] : null}</div>
                    </div>
                </CardContent>
                <CardFooter className="sm:flex items-center justify-between block space-y-2">
                    <Button className="sm:w-auto order-last w-full " onClick={handleSubmit} disabled={isLoading}> {isLoading ? <> <Loading width={16} height={16} color="#fff" /> Creating account...</> : 'Create Account'}</Button>
                    <Link to="/signin" className='text-[13px]  order-1 sm:order-3 '>Already have an account? <span className='text-sm font-semibold hover:underline text-gray-800' >Sign In</span> </Link>
                </CardFooter>
            </Card>
        </div>
    )
}

export default Register